import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { cachedGet, api, errMsg } from "@/lib/api";
import { INDUSTRIES } from "@/data/industries";
import { PageTitle, PctBar, RagBadge, Variance, fmtDate, Btn, Pick, Loading, Stat } from "@/components/app/bits";

function NewProject({ open, setOpen, onDone }) {
  const [f, setF] = useState({ name: "", code: "", industry: "", description: "" });
  const save = async () => {
    try { await api.post("/projects", f); toast.success("Project created"); setOpen(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0F172A] border-[#1E2D4A]" data-testid="new-project-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">New project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Project name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-project-name" />
          <Input placeholder="Project code, e.g. ABC-01" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="new-project-code" />
          <Pick testid="new-project-industry" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} placeholder="Industry" options={INDUSTRIES.map((i) => [i.slug, i.name])} />
          <Btn onClick={save} disabled={!f.name || !f.code} data-testid="new-project-save" className="w-full">Create project</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Portfolio() {
  const { me, can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => cachedGet("/projects").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { setRows(null); load(); }, [load, me.active_org_id]);
  if (!rows) return <Loading />;
  const red = rows.filter((r) => r.rag === "red").length;
  return (
    <div data-testid="portfolio-page">
      <PageTitle eyebrow="Portfolio" title={me.orgs.find((o) => o.id === me.active_org_id)?.name}>
        {can("Manager") && <Btn onClick={() => setOpen(true)} data-testid="new-project-btn"><Plus className="w-4 h-4" />New project</Btn>}
      </PageTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Projects" value={rows.length} testid="stat-projects" />
        <Stat label="Tags" value={rows.reduce((a, r) => a + r.tags, 0).toLocaleString("en-GB")} testid="stat-tags" />
        <Stat label="Open cat A punch" value={rows.reduce((a, r) => a + r.open_cat_a, 0)} accent="text-red-400" testid="stat-open-a" />
        <Stat label="Projects red" value={red} accent={red ? "text-red-400" : "text-emerald-400"} testid="stat-red" />
      </div>
      <div className="md:hidden space-y-3" data-testid="portfolio-cards">
        {rows.map((p) => (
          <button key={p.id} onClick={() => nav(`/app/projects/${p.id}`)} data-testid={`portfolio-card-${p.code}`} className="panel p-4 w-full text-left space-y-3">
            <div className="flex justify-between gap-2"><div><p className="font-semibold text-slate-100">{p.name}</p><p className="font-mono text-xs text-slate-500">{p.code} · {p.tags} tags</p></div><RagBadge rag={p.rag} /></div>
            <PctBar pct={p.pct} />
            {p.key_milestone && <p className="text-xs text-slate-400">{p.key_milestone.name}: <span className="num">{fmtDate(p.key_milestone.target_date)}</span> → <span className="num text-amber-400">{fmtDate(p.key_milestone.forecast_date)}</span> <Variance days={p.key_milestone.variance_days} /></p>}
            <p className="text-xs text-red-400 num">{p.open_cat_a} open cat A</p>
          </button>
        ))}
      </div>
      <div className="panel overflow-x-auto hidden md:block">
        <table className="w-full text-sm min-w-[900px]" data-testid="portfolio-table">
          <thead><tr className="border-b border-[#1E2D4A] text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
            {["Project", "Tags", "% complete", "Open cat A", "Key milestone", "Target", "Forecast", "Variance", "Status"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} onClick={() => nav(`/app/projects/${p.id}`)} data-testid={`portfolio-row-${p.code}`} className="border-b border-[#1E2D4A] last:border-0 hover:bg-[#131C31] cursor-pointer transition-colors">
                <td className="px-4 py-4"><p className="font-semibold text-slate-100">{p.name}</p><p className="font-mono text-xs text-slate-500">{p.code} · {INDUSTRIES.find((i) => i.slug === p.industry)?.name || "-"}</p></td>
                <td className="px-4 num text-slate-300">{p.tags.toLocaleString("en-GB")}</td>
                <td className="px-4 w-40"><PctBar pct={p.pct} /></td>
                <td className="px-4 num text-red-400 font-semibold">{p.open_cat_a}</td>
                <td className="px-4 text-slate-200">{p.key_milestone?.name || "-"}</td>
                <td className="px-4 num text-slate-300">{fmtDate(p.key_milestone?.target_date)}</td>
                <td className="px-4 num text-amber-400">{fmtDate(p.key_milestone?.forecast_date)}</td>
                <td className="px-4"><Variance days={p.key_milestone?.variance_days} /></td>
                <td className="px-4"><RagBadge rag={p.rag} testid={`portfolio-rag-${p.code}`} /></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No projects yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <NewProject open={open} setOpen={setOpen} onDone={load} />
    </div>
  );
}
